import React, { useState } from 'react';
import { Rapport, UpdateRapportRequest } from '../../services/reportService';
import { useModal } from '../../contexts/ModalContext';
import ReportEditForm from './ReportEditForm';
import Pagination from '../Common/Pagination';
import { usePagination } from '../../hooks/usePagination';
import './ReportsTable.css';

interface ReportsTableProps {
  reports: Rapport[];
  onUpdateReport: (id: number, data: UpdateRapportRequest) => Promise<void>;
  onReplaceFile: (id: number, file: File) => Promise<void>;
  onToggleStatus: (id: number) => Promise<void>;
  onDeleteReport: (id: number) => Promise<void>;
  onDownloadReport: (id: number, nomRapport: string) => Promise<void>;
  loading?: boolean;
}

const ReportsTable: React.FC<ReportsTableProps> = ({
  reports,
  onUpdateReport,
  onReplaceFile,
  onToggleStatus,
  onDeleteReport,
  onDownloadReport,
  loading = false
}) => {
  const [editingReport, setEditingReport] = useState<Rapport | null>(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>('all');
  const { showModal } = useModal();

  // Filtrer les rapports
  const filteredReports = reports.filter(report => {
    const matchesSearch = report.nomRapport.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.dossier.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (report.description && report.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && !report.isDisabled) ||
                         (statusFilter === 'disabled' && report.isDisabled);
    
    return matchesSearch && matchesStatus;
  });

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    totalItems,
    setCurrentPage,
    setItemsPerPage
  } = usePagination({
    data: filteredReports,
    itemsPerPage: 10,
    initialPage: 1
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleEdit = (report: Rapport) => {
    setEditingReport(report);
  };

  const handleUpdateReport = async (data: UpdateRapportRequest) => {
    if (!editingReport) return;
    
    setUpdating(true);
    try {
      await onUpdateReport(editingReport.id, data);
      setEditingReport(null);
    } finally {
      setUpdating(false);
    }
  };

  const handleReplaceFile = async (file: File) => {
    if (!editingReport) return;
    
    setUpdating(true);
    try {
      await onReplaceFile(editingReport.id, file);
      setEditingReport(null);
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleStatus = async (id: number) => {
    const report = reports.find(r => r.id === id);
    const action = report?.isDisabled ? 'activer' : 'désactiver';
    
    showModal({
      title: `Confirmer ${action}`,
      message: `Êtes-vous sûr de vouloir ${action} le rapport "${report?.nomRapport}" ?`,
      type: 'confirm',
      confirmText: action === 'activer' ? 'Activer' : 'Désactiver',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await onToggleStatus(id);
        } catch (err: any) {
          showModal({
            title: 'Erreur de changement de statut',
            message: err.response?.data?.message || 'Erreur lors du changement de statut',
            type: 'error',
            confirmText: 'OK'
          });
        }
      }
    });
  };

  const handleDelete = async (id: number) => {
    const report = reports.find(r => r.id === id);
    showModal({
      title: 'Confirmer la suppression',
      message: `Êtes-vous sûr de vouloir supprimer le rapport "${report?.nomRapport}" ? Cette action est irréversible.`,
      type: 'confirm',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await onDeleteReport(id);
        } catch (err: any) {
          showModal({
            title: 'Erreur de suppression',
            message: err.response?.data?.message || 'Erreur lors de la suppression du rapport',
            type: 'error',
            confirmText: 'OK'
          });
        }
      }
    });
  };

  if (loading) {
    return <div className="loading">Chargement des rapports...</div>;
  }

  return (
    <div className="reports-table-container">
      {editingReport && (
        <ReportEditForm
          report={editingReport}
          onSubmit={handleUpdateReport}
          onCancel={() => setEditingReport(null)}
          onReplaceFile={handleReplaceFile}
          loading={updating}
        />
      )}

      <div className="table-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Rechercher par nom, dossier ou description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-container">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'disabled')}
            className="filter-select"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Activés uniquement</option>
            <option value="disabled">Désactivés uniquement</option>
          </select>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="reports-table">
          <thead>
            <tr>
              <th>Nom du rapport</th>
              <th>Dossier</th>
              <th>Fichier</th>
              <th>Statut</th>
              <th>Taille</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((report) => (
              <tr key={report.id}>
                <td>
                  <div className="report-name">
                    <strong>{report.nomRapport}</strong>
                    {report.description && (
                      <small className="report-description">{report.description}</small>
                    )}
                  </div>
                </td>
                <td>{report.dossier}</td>
                <td>
                  <span className="file-path" title={report.fichiers}>
                    {report.fichiers.split('\\').pop() || report.fichiers}
                  </span>
                </td>
                <td>
                  <span className={`status ${report.isDisabled ? 'disabled' : 'active'}`}>
                    {report.isDisabled ? 'Désactivé' : 'Activé'}
                  </span>
                </td>
                <td>{formatFileSize(report.tailleFichier)}</td>
                <td>
                  <div className="table-actions">
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(report)}
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button
                      className="action-btn toggle"
                      onClick={() => handleToggleStatus(report.id)}
                      title={report.isDisabled ? "Activer" : "Désactiver"}
                    >
                      {report.isDisabled ? "🔓" : "🔒"}
                    </button>
                    <button
                      className="action-btn download"
                      onClick={() => onDownloadReport(report.id, report.nomRapport)}
                      title="Télécharger"
                    >
                      📥
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(report.id)}
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginatedData.length === 0 && (
        <div className="empty-state">
          {searchTerm || statusFilter !== 'all' 
            ? 'Aucun rapport ne correspond aux critères de recherche.'
            : 'Aucun rapport trouvé.'
          }
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onItemsPerPageChange={setItemsPerPage}
      />
    </div>
  );
};

export default ReportsTable;
